import { ref, type Ref, computed } from 'vue';
import { useCustomer } from '@/use/customer';
import { dropoffAddress, pickupAddress, serviceType, rideType } from '@/use/registration';
import { useAddress } from '@/use/address';
import { useGrocery } from '@/use/grocery';
import type { FollowUpRequest } from '@/models/followUpRequest';
import { RideServiceType } from '@/models/ride';
import { rideTypes } from '@/constants/ride';

const { leadInfo, setLeadInfo } = useCustomer();

export { leadInfo, setLeadInfo };
export const lead = computed(() => leadInfo.value?.lead ?? null);
export const leadCaller = computed(() => leadInfo.value?.caller ?? null);
export const isFacebookLeadSource = computed(
  () =>
    lead.value?.source === LeadSource.FacebookSalesCampaign ||
    lead.value?.source === LeadSource.FacebookSalesPhone ||
    lead.value?.source === LeadSource.FacebookLeadsPhone,
);

export const isAllowedLeadRegistration = computed(() => {
  if (!lead.value) {
    return false;
  }

  const isFacebookLead = !!lead.value.facebookLeadId;
  if (!isFacebookLead && !isFacebookLeadSource.value) {
    return false;
  }

  return leadInfo.value?.shouldDisplayLeadContext ?? false;
});

export enum LeadRegistrationSteps {
  LeadContext = 0,
  ChooseService = 1,
  DetermineEstimate = 2,
  Estimate = 3,
  DetermineIntent = 4,
  ScheduleFollowUp = 5,
}

export enum CustomerService {
  Rides = 'rides',
  Gourmet = 'gourmet',
  Grocery = 'grocery',
  HomeServices = 'homeServices',
  PharmacyPickup = 'pharmacyPickup',
}

export enum LeadSource {
  FacebookSalesCampaign = 'facebook-sales-campaign',
  FacebookSalesPhone = 'facebook-sales-phone',
  FacebookLeadsPhone = 'facebook-leads-phone',
}

export enum NeededService {
  Ride = 'rides_or_transportation',
  Delivery = 'food_or_grocery_delivery',
  Pharmacy = 'pharmacy_or_medication_delivery',
  HomeService = 'help_at_home_cleaning_caregiver_repairs',
}

export const serviceList = [
  { key: CustomerService.Rides, label: 'Rides' },
  { key: CustomerService.Gourmet, label: 'Gourmet / Meal Delivery' },
  { key: CustomerService.Grocery, label: 'Grocery Delivery' },
  { key: CustomerService.HomeServices, label: 'Home Services' },
  { key: CustomerService.PharmacyPickup, label: 'Pharmacy Pickup' },
];

export const reasonForCallingOptions = [
  { id: 1, label: 'Rides', key: NeededService.Ride },
  { id: 2, label: 'Food or Grocery Delivery', key: NeededService.Delivery },
  { id: 3, label: 'Pharmacy Pickup', key: NeededService.Pharmacy },
  { id: 4, label: 'Home Services', key: NeededService.HomeService },
];

export const estimationService: Ref<CustomerService | null> = ref(null);
export const followUpRequest: Ref<FollowUpRequest | null> = ref(null);
export const followUpRequestResult = ref('');

export const resetRegistrationData = () => {
  const { setRegistrationStartAddress } = useAddress();
  const { setGroceryAddress } = useGrocery();

  estimationService.value = null;
  followUpRequestResult.value = '';

  pickupAddress.value = null;
  dropoffAddress.value = null;
  serviceType.value = RideServiceType.Transportation;
  rideType.value = rideTypes[0].key;
  setRegistrationStartAddress(null);
  setGroceryAddress(null);
};
